export const hasValidTesseramentoForCurrentYear = (userData) => {
  if (!userData?.tesseramenti || userData.tesseramenti.length === 0) {
    return false;
  }
  const currentYear = new Date().getFullYear();
  return userData.tesseramenti.some((socio) => {
    const tesseramentiSocio =
      socio.tesseramenti && Array.isArray(socio.tesseramenti)
        ? socio.tesseramenti
        : [];
    return tesseramentiSocio.some(
      (t) =>
        t.anno === currentYear &&
        (t.paymentStatus === 'succeeded' || t.paymentStatus === 'pending')
    );
  });
};

export const hasPendingTesseramenti = (userData) => {
  if (!userData?.tesseramenti?.length) return false;
  return userData.tesseramenti.some((socio) => {
    const tess = Array.isArray(socio.tesseramenti) ? socio.tesseramenti : [];
    return tess.some((t) => t.paymentStatus === 'pending');
  });
};

export const isGuestBookingEvent = (event) =>
  event?.tipoIscrizione === 'aperto_tutti';

// Prezzo vuoto non è gratis (Number(null) === 0): come il server, è un prezzo mancante.
export const isFreeGuestEvent = (event) =>
  event?.is_free === true ||
  (event?.prezzo !== null &&
    event?.prezzo !== undefined &&
    event?.prezzo !== '' &&
    Number(event.prezzo) === 0);

export const parseBirthDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const isMinorBirthDate = (value) => {
  const d = parseBirthDate(value);
  if (!d) return false;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age < 18;
};

export const isOrderCancelled = (order) =>
  order?.metadata?.products?.some((product) => product.isCancelled === true);

export const isGuestOrder = (order) =>
  order?.participantType === 'esterno' ||
  order?.metadata?.participantType === 'esterno';

export const isOrderInactiveForSeatCount = (order) => {
  if (isOrderCancelled(order)) return true;
  const status = order?.paymentStatus;
  return status === 'failed' || status === 'refunded';
};

const VARIANT_AUDIENCES = ['solo_soci', 'aperto_tutti'];

/** Chi può prenotare la variante: il valore esplicito vince, "eredita" (o vuoto) segue l'evento. */
export const resolveVariantAudience = (variant, event) => {
  if (VARIANT_AUDIENCES.includes(variant?.prenotabileDa)) {
    return variant.prenotabileDa;
  }
  return isGuestBookingEvent(event) ? 'aperto_tutti' : 'solo_soci';
};

export const isVariantOpenToGuests = (variant, event) =>
  Boolean(variant) &&
  variant.visibilitaSito !== 'nascosto' &&
  resolveVariantAudience(variant, event) === 'aperto_tutti';

// Stessa regola di filterFutureObjects: conta la fine, altrimenti l'inizio.
const isVariantPast = (variant, now = new Date()) => {
  const end = variant?.Data_Fine ? new Date(variant.Data_Fine) : null;
  if (end && !Number.isNaN(end.getTime())) return end < now;
  const start = variant?.Data_inizio ? new Date(variant.Data_inizio) : null;
  if (start && !Number.isNaN(start.getTime())) return start < now;
  return false;
};

const hasVariants = (event) =>
  Array.isArray(event?.Varianti) && event.Varianti.length > 0;

/** Varianti che un ospite può scegliere, in ordine di data. */
export const getGuestBookableVariants = (event) =>
  (event?.Varianti || [])
    .filter((v) => isVariantOpenToGuests(v, event) && !isVariantPast(v))
    .sort((a, b) => (a.Data_inizio || '').localeCompare(b.Data_inizio || ''));

const isProductLineInactive = (p) =>
  p?.isCancelled === true || p?.isRefunded === true || p?.refunded === true;

/**
 * Posti occupati su una variante: soci unici più tutti i partecipanti degli ordini ospite.
 * Il saldo completa un acconto già contato, quindi non occupa un posto.
 * Deve restare allineata a countVariantOccupiedSeats del server.
 */
export const countVariantOccupiedSeats = (variant) => {
  const variantId = variant?.documentId;
  if (!variantId) return 0;
  const socios = new Set();
  let socioOrders = 0;
  let guestSeats = 0;
  (variant.Ordini || []).forEach((order) => {
    // Solo lo stato: una riga annullata su un'altra variante non libera questa.
    if (['failed', 'refunded'].includes(order?.paymentStatus)) return;
    const line = (order?.metadata?.products || []).find(
      (p) =>
        // L'id basta: ordini vecchi possono non avere type sulla riga
        p?.id === variantId &&
        ['acconto', 'totale'].includes(p?.paymentType) &&
        !isProductLineInactive(p)
    );
    if (!line) return;
    if (isGuestOrder(order)) {
      const participants = Array.isArray(order.participants)
        ? order.participants.length
        : 0;
      guestSeats +=
        participants || (Number(line.quantity) > 0 ? Number(line.quantity) : 1);
      return;
    }
    socioOrders += 1;
    const socioId = order.socio?.documentId || order.socio?.email;
    if (socioId) socios.add(socioId);
  });
  // Senza socio popolato (es. liste eventi) un ordine vale un posto
  return (socios.size || socioOrders) + guestSeats;
};

/** Posti liberi sulla variante; 999 se non ha capienza (come per gli eventi). */
export const computeVariantAvailableSeats = (variant) => {
  const capienza = Number(variant?.Capienza);
  if (!(capienza > 0)) return 999;
  return Math.max(0, capienza - countVariantOccupiedSeats(variant));
};

export const guestBookingSupportedForEvent = (event) => {
  if (event?.externalURL) return false;
  if (hasVariants(event)) return getGuestBookableVariants(event).length > 0;
  if (!isGuestBookingEvent(event)) return false;
  if (isFreeGuestEvent(event)) return true;
  const price = Number(event?.prezzo);
  if (!Number.isFinite(price) || price <= 0) return false;
  return true;
};

export const shouldUseGuestBookingFlow = (
  event,
  userData,
  { registrationOpen, isEarlyAccessOnly }
) => {
  if (!guestBookingSupportedForEvent(event)) return false;
  if (!registrationOpen) return false;
  if (isEarlyAccessOnly) return false;
  if (hasValidTesseramentoForCurrentYear(userData)) return false;
  return true;
};

export const countGuestOrderSeats = (order) => {
  if (Array.isArray(order?.participants) && order.participants.length > 0) {
    return order.participants.length;
  }
  const product = order?.metadata?.products?.find((p) => p.type === 'event');
  if (product?.quantity != null && product.quantity > 0) {
    return product.quantity;
  }
  return 1;
};

export const countEventOccupiedSeats = (event) => {
  const orders = (event?.orders || []).filter(
    (order) => !isOrderInactiveForSeatCount(order)
  );

  const nonGuestOrders = orders.filter((order) => !isGuestOrder(order));
  const hasSocioData = nonGuestOrders.some(
    (order) => order.socio?.documentId || order.socio?.email
  );

  let guestSeats = 0;
  orders.forEach((order) => {
    if (isGuestOrder(order)) {
      guestSeats += countGuestOrderSeats(order);
    }
  });

  if (hasSocioData) {
    const uniqueSocios = new Set();
    nonGuestOrders.forEach((order) => {
      const socioId = order.socio?.documentId || order.socio?.email;
      if (socioId) uniqueSocios.add(socioId);
    });
    return uniqueSocios.size + guestSeats;
  }

  return nonGuestOrders.length + guestSeats;
};

/**
 * Capienza usata per i posti.
 * Quella dell'evento, se c'è. Altrimenti quella dell'unico modulo in programma.
 */
export const capienzaEvento = (event) => {
  const own = Number(
    event?.capienza || event?.Capienza || event?.Max_Participants
  );
  if (Number.isFinite(own) && own > 0) return own;
  const items = event?.programma;
  if (!Array.isArray(items) || items.length !== 1) return null;
  const fromModule = Number(items[0]?.capienza);
  return Number.isFinite(fromModule) && fromModule > 0 ? fromModule : null;
};

export const computeGuestAvailableSeats = (event) => {
  const capienza = capienzaEvento(event);
  if (!capienza) return 999;
  return Math.max(0, capienza - countEventOccupiedSeats(event));
};
